/**
 * SentenceView Component
 * 
 * Renders a sentence-by-sentence view with clickable tokens and TTS controls.
 */

import React from 'react';
import {
  Play,
  Pause,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

/**
 * SentenceControls - Navigation and playback controls for sentence mode
 */
export function SentenceControls({
  currentIndex,
  totalSentences,
  isPlaying,
  onPrev,
  onNext,
  onPlayPause,
}) {
  return (
    <div className="flex items-center justify-center gap-4 py-3 border-b bg-muted/30">
      <Button
        variant="outline"
        size="sm"
        onClick={onPrev}
        disabled={currentIndex === 0}
        data-testid="prev-sentence-btn"
      >
        <ChevronUp className="h-4 w-4" />
      </Button>
      
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPlayPause}
          data-testid="play-sentence-btn"
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>
        <span className="text-sm text-muted-foreground min-w-[60px] text-center">
          {currentIndex + 1} / {totalSentences}
        </span>
      </div>
      
      <Button
        variant="outline"
        size="sm"
        onClick={onNext}
        disabled={currentIndex >= totalSentences - 1}
        data-testid="next-sentence-btn"
      >
        <ChevronDown className="h-4 w-4" />
      </Button>
    </div>
  );
}

/**
 * SentenceTokenDisplay - Renders tokens within a sentence with click handlers
 */
export function SentenceTokenDisplay({
  tokens,
  selectedToken,
  onTokenClick,
  getTokenStyle,
  fontSize,
  textDirection,
  isArabicScript,
}) {
  if (!tokens || tokens.length === 0) {
    return (
      <p className="text-center text-muted-foreground py-8">
        No sentence data available
      </p>
    );
  }

  return (
    <div
      className="text-center py-8 px-4"
      style={{
        direction: textDirection,
        fontSize: `${fontSize}px`,
        lineHeight: 2,
      }}
    >
      {tokens.map((token, idx) => {
        const isSelected =
          selectedToken?.token_id != null &&
          token.token_id != null &&
          selectedToken.token_id === token.token_id;

        // Determine if this is a clickable word or punctuation/whitespace
        const isWord = token.is_word !== false && !/^[\s\p{P}]+$/u.test(token.surface_form);

        if (!isWord) {
          // Render punctuation/whitespace as-is
          return (
            <span key={`sent-punct-${idx}`} style={{ whiteSpace: 'pre-wrap' }}>
              {token.surface_form}
            </span>
          );
        }

        return (
          <span
            key={`sent-word-${token.token_id || idx}`}
            onClick={() => onTokenClick(token)}
            className={`reader-word cursor-pointer ${isSelected ? 'selected' : ''}`}
            style={{
              ...getTokenStyle(token),
              fontFamily: isArabicScript ? "'Noto Naskh Arabic', 'Traditional Arabic', serif" : 'inherit',
            }}
            data-testid={`sentence-token-${idx}`}
          >
            {token.surface_form}
          </span>
        );
      })}
    </div>
  );
}

/**
 * useSentenceRanges - Hook to calculate sentence boundaries from tokens
 */
export function calculateSentenceRanges(tokens) {
  if (!tokens || tokens.length === 0) return [];

  const ranges = [];
  let sentenceStart = 0;

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    const text = t.surface_form || '';
    
    // Check for sentence-ending punctuation
    if (/[.!?。！？]$/.test(text.trim())) {
      const sentenceTokens = tokens.slice(sentenceStart, i + 1);
      const sentenceText = sentenceTokens
        .map(tok => tok.surface_form)
        .join('')
        .trim();
      
      if (sentenceText) {
        ranges.push({
          start: sentenceStart,
          end: i + 1,
          text: sentenceText,
        });
      }
      sentenceStart = i + 1;
    }
  }

  // Handle remaining tokens after last sentence-ender
  if (sentenceStart < tokens.length) {
    const sentenceTokens = tokens.slice(sentenceStart);
    const sentenceText = sentenceTokens
      .map(tok => tok.surface_form)
      .join('')
      .trim();
    
    if (sentenceText) {
      ranges.push({
        start: sentenceStart,
        end: tokens.length,
        text: sentenceText,
      });
    }
  }

  // If no boundaries found, treat whole text as one sentence
  if (ranges.length === 0) {
    ranges.push({
      start: 0,
      end: tokens.length,
      text: tokens.map(t => t.surface_form).join('').trim(),
    });
  }

  return ranges;
}

/**
 * TTS Language mapping for sentence playback
 */
export const TTS_LANG_MAP = {
  pt: 'pt-BR',
  por: 'pt-BR',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  deu: 'de-DE',
  ar: 'ar-SA',
  fa: 'fa-IR',
  zh: 'zh-CN',
  ja: 'ja-JP',
  ko: 'ko-KR',
  ru: 'ru-RU',
  it: 'it-IT',
  nl: 'nl-NL',
  tr: 'tr-TR',
  he: 'he-IL',
  hi: 'hi-IN',
};

export default {
  SentenceControls,
  SentenceTokenDisplay,
  calculateSentenceRanges,
  TTS_LANG_MAP,
};


